import { useSuspenseQuery } from '@apollo/client';
import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet } from 'react-router-dom';

import {
  Heading,
  Icon,
  LinkButton,
  LoadingIndicator,
  Stack,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin, useIsMlEngineer } from '@eluve/session-helpers';
import { TabNav } from '@eluve/smart-blocks';

import {
  appointmentTagOptionsFragment,
  reviewerLowQualityFactorsFragment,
  reviewerPromptTemplatesFragment,
  reviewerTenantsFragment,
  reviewerUsersFragment,
} from './operations';

export interface ReviewLayoutProps {}

const persistentQueryParams = ['tenantId', 'userId'];

const reviewerRoutes = [
  {
    name: 'Appointments',
    link: 'appointments',
    persistentQueryParams,
  },
  {
    name: 'Feedback',
    link: 'feedback',
    persistentQueryParams,
  },
];

const adminReviewerRoutes = [
  ...reviewerRoutes,
  {
    name: 'Admin Export',
    link: 'admin/export',
    persistentQueryParams,
  },
];

const baseReviewerDataQuery = graphql(
  `
    query getBaseReviewerData {
      ...ReviewerTenants
      ...ReviewerUsers
      ...ReviewerPromptTemplates
      ...ReviewerLowQualityFactors
      ...TagOptions
    }
  `,
  [
    reviewerTenantsFragment,
    reviewerUsersFragment,
    reviewerLowQualityFactorsFragment,
    reviewerPromptTemplatesFragment,
    appointmentTagOptionsFragment,
  ],
);

export const ReviewLayout: React.FC<ReviewLayoutProps> = () => {
  useSuspenseQuery(baseReviewerDataQuery);
  const isEluveAdmin = useIsEluveAdmin();
  const isMlEngineer = useIsMlEngineer();
  const routes =
    isEluveAdmin || isMlEngineer ? adminReviewerRoutes : reviewerRoutes;
  return (
    <>
      <Helmet>
        <title>Review | Eluve</title>
      </Helmet>
      <div className="min-h-full overflow-x-hidden">
        <div className="bg-text-primary pb-32">
          <header className="py-4 lg:py-8">
            <div className="mx-auto w-full px-2 lg:px-8">
              <Stack
                className="gap-y-2"
                direction="horizontal"
                wrap
                align="center"
                justify="between"
              >
                <Stack
                  direction="horizontal"
                  align="center"
                  gap={2}
                  fullWidth={false}
                >
                  <Icon
                    name="UserRoundSearch"
                    size="xl"
                    className="text-border-yellow-subtle"
                    style={{ width: '2.5rem', height: '2.5rem' }}
                  />
                  <Heading
                    size="3xl"
                    weight="bold"
                    level={1}
                    className="tracking-tight text-white"
                  >
                    Eluve Review Dashboard
                  </Heading>
                </Stack>
                <Stack
                  className="md:gap-2"
                  direction="horizontal"
                  wrap
                  gap={1}
                  align="stretch"
                >
                  <LinkButton variant="secondary" to="/">
                    <Icon name="ArrowLeft" size="auto" />
                    Return to App
                  </LinkButton>
                  <LinkButton variant="secondary" to="/sign-out">
                    <Icon name="LogOut" size="auto" />
                    Sign out
                  </LinkButton>
                </Stack>
              </Stack>
              <div className="mt-4">
                <TabNav tabs={routes} colorMode="dark" />
              </div>
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="mx-auto w-full px-2 pb-12 lg:px-8">
            <div className="min-h-96 rounded-lg bg-white px-5 py-6 shadow-sm sm:px-6">
              <div className="w-full">
                <Suspense fallback={<LoadingIndicator size="xl" />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </div>
        </main>
        <div id="portal-footer" />
      </div>
    </>
  );
};
